import React, { useState } from 'react';
import { ContractAnalysisJSON } from '../../types/contract.types';
import { ContractAnalysisJsonParser } from '../../services/parsers/contractAnalysisJsonParser';
import DetailedClauseReview from './clauses/DetailedClauseReview';
import { FileText, Upload } from 'lucide-react';

const TestJsonLoader: React.FC = () => {
  const [analysis, setAnalysis] = useState<ContractAnalysisJSON | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const fileContent = await file.text();
      const parsedAnalysis = await ContractAnalysisJsonParser.parseJsonAnalysis(fileContent);
      setAnalysis(parsedAnalysis);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse JSON');
    } finally {
      setLoading(false);
    }
  };

  // Removed sample file loader to avoid mock data usage
  const loadSampleFile = async () => {
    setError('Sample file loading disabled. Please upload a real analysis JSON.');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Enhanced Contract Analysis Viewer</h1>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Upload Analysis JSON</h2>
            <button
              onClick={loadSampleFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              disabled={loading}
            >
              <FileText className="w-4 h-4 mr-2" />
              Load Sample File
            </button>
          </div>

          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-10 h-10 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">JSON files only</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".json"
                onChange={handleFileUpload}
                disabled={loading}
              />
            </label>
          </div>

          {loading && (
            <div className="mt-4 text-center text-gray-600">Loading...</div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Analysis Display */}
        {analysis && (
          <div>
            {/* Executive Summary */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
              <p className="text-gray-700 mb-4">{analysis.executive_summary.overview}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {analysis.executive_summary.critical_issues.map((issue, index) => (
                  <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <span className="text-xl mr-2">{issue.icon}</span>
                      <h3 className="font-semibold text-red-900">{issue.title}</h3>
                    </div>
                    <p className="text-sm text-red-700 mb-2">{issue.description}</p>
                    <p className="text-xs text-red-600 italic">Action: {issue.recommended_action}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <span className="text-sm text-gray-600">Overall Favorability Score: </span>
                <span className="text-2xl font-bold text-blue-600">
                  {analysis.executive_summary.overall_favorability_score}/10
                </span>
              </div>
            </div>

            {/* Clauses */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Contract Clauses ({analysis.clauses.length})
              </h2>
              <div className="space-y-4">
                {analysis.clauses.slice(0, 5).map((clause) => (
                  <DetailedClauseReview
                    key={clause.reference}
                    clause={clause}
                    contractId="test-contract"
                    analysisId="test-analysis"
                  />
                ))}
              </div>
              
              {analysis.clauses.length > 5 && (
                <div className="mt-6 text-center text-gray-600">
                  Showing first 5 of {analysis.clauses.length} clauses
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestJsonLoader; 